import { AutocompleteCloseReason } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReferenceOption } from '../../form/forms';
import { syncUnresolvedChipKeysSelection } from '../utils/autocompleteChipResolutionUtils';

type UseAutocompleteAIFlowArgs = {
    /** Enables multi-select behavior and multi-chip AI resolution flow. */
    multiple: boolean;
    /** Indicates the field is currently in AI-extracted mode. */
    isAIExtractedData: boolean;
    /** Current autocomplete value from the controller (single or multiple). */
    val: any;
    /** Form field name, used when reporting AI resolution completion. */
    name: string;
    /** Optional callback invoked when AI data for this field is resolved. */
    updateAIDataResolved?: (name: string) => void;
    /** Computes stable chip keys for the current selected options. */
    getChipKeys: (selectedOptions: ReferenceOption[]) => string[];
    /** Triggers option loading for a search term with optional force and filter. */
    updateOptions: (term, force?: boolean, filter?) => void;
    /** Effective search filter to use for option loading. */
    effectiveFilter: any;
    /** Controls initial preload-search behavior. */
    preload: boolean;
    /** Initial search query term supplied by the field config. */
    queryTerm: string;
    /** Flags whether unresolved AI chips should use highlighted styling. */
    requiresUserCorrection: boolean;
    /** Theme color map used for AI-highlighted and normal chip styles. */
    aiExtractedFieldColor: Record<string, string>;
    /** Mirrors Autocomplete disableCloseOnSelect behavior for close handling. */
    disableCloseOnSelect: boolean;
    /** Sets available options in the parent controller state. */
    setOptions: (options: ReferenceOption[]) => void;
    /** Sets the current text input value in the parent controller state. */
    setInputValue: (value: string) => void;
};

export const useAutocompleteAIFlow = ({
    multiple,
    isAIExtractedData,
    val,
    name,
    updateAIDataResolved,
    getChipKeys,
    updateOptions,
    effectiveFilter,
    preload,
    queryTerm,
    requiresUserCorrection,
    aiExtractedFieldColor,
    disableCloseOnSelect,
    setOptions,
    setInputValue
}: UseAutocompleteAIFlowArgs) => {
    const aiLifecycleRef = useRef({
        hasInitializedMultipleAIState: false,
        hasResolvedMultipleAIField: false,
        lastSingleAIPrefetchTerm: null as string | null,
        hasInitializedRegularFlowPrefetch: false,
        hasInitializedPreloadSearch: false,
        skipNextOpenEmptyQueryFetch: false
    });

    const [isAIExtractedValue, setIsAIExtractedValue] = useState<boolean>(isAIExtractedData);
    const [unresolvedChipKeys, setUnresolvedChipKeys] = useState<string[]>([]);
    const [editingChipKey, setEditingChipKey] = useState<string | null>(null);
    const [editingChipSearchTerm, setEditingChipSearchTerm] = useState('');
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

    const unresolvedChipKeySet = useMemo(() => new Set(unresolvedChipKeys), [unresolvedChipKeys]);
    const isMultipleAIFieldActive = multiple && isAIExtractedData && unresolvedChipKeys.length > 0;
    const isAIFieldActive = multiple ? isMultipleAIFieldActive : isAIExtractedValue;

    useEffect(() => {
        setIsAIExtractedValue(isAIExtractedData);
    }, [isAIExtractedData]);

    const syncUnresolvedChipKeys = useCallback(
        (selectedOptions: ReferenceOption[]) => {
            setUnresolvedChipKeys((previous) =>
                syncUnresolvedChipKeysSelection(selectedOptions, previous, getChipKeys)
            );
        },
        [getChipKeys]
    );

    const clearChipEditingContext = useCallback(() => {
        setEditingChipKey(null);
        setEditingChipSearchTerm('');
    }, []);

    const onAutocompleteOpen = useCallback(() => {
        setIsAutocompleteOpen(true);
        const lifecycle = aiLifecycleRef.current;

        // Never show stale options when reopening during/after AI multi-chip resolution flow.
        if (multiple && lifecycle.hasInitializedMultipleAIState) {
            setOptions([]);
        }

        // Chip-click flow already triggered a targeted search; avoid overriding it.
        if (lifecycle.skipNextOpenEmptyQueryFetch) {
            lifecycle.skipNextOpenEmptyQueryFetch = false;
            return;
        }

        // Field click should reset chip-edit context and query from empty text.
        if (editingChipKey) {
            clearChipEditingContext();
        }
    }, [clearChipEditingContext, editingChipKey, multiple, setOptions]);

    const onAutocompleteClose = useCallback(
        (_event, reason: AutocompleteCloseReason) => {
            setIsAutocompleteOpen(false);

            if (multiple && isAIFieldActive && (reason === 'blur' || reason === 'toggleInput')) {
                // Closing the list without selection should not preserve stale suggestions.
                setOptions([]);
            }

            if (multiple && disableCloseOnSelect && reason === 'blur') {
                setInputValue('');
            }

            if (reason === 'blur' || reason === 'toggleInput') {
                clearChipEditingContext();
            }
        },
        [clearChipEditingContext, disableCloseOnSelect, isAIFieldActive, multiple, setInputValue, setOptions]
    );

    const onUnresolvedChipClick = useCallback(
        (chip: ReferenceOption, chipKey: string) => {
            if (!multiple || !isAIFieldActive || !unresolvedChipKeySet.has(chipKey)) {
                return;
            }

            const searchTerm = chip?.label || '';
            setEditingChipKey(chipKey);
            setEditingChipSearchTerm(searchTerm);
            setInputValue('');
            setOptions([]);
            aiLifecycleRef.current.skipNextOpenEmptyQueryFetch = true;
            setIsAutocompleteOpen(true);
            updateOptions(searchTerm, true, effectiveFilter);
        },
        [effectiveFilter, isAIFieldActive, multiple, unresolvedChipKeySet, setInputValue, setOptions, updateOptions]
    );

    useEffect(() => {
        if (!multiple) {
            return;
        }

        const lifecycle = aiLifecycleRef.current;

        if (!isAIExtractedData) {
            lifecycle.hasInitializedMultipleAIState = false;
            lifecycle.hasResolvedMultipleAIField = false;
            setUnresolvedChipKeys((previous) => (previous.length ? [] : previous));
            return;
        }

        if (!lifecycle.hasInitializedMultipleAIState) {
            if (!Array.isArray(val) || val.length === 0) {
                return;
            }

            lifecycle.hasInitializedMultipleAIState = true;
            setUnresolvedChipKeys(getChipKeys(val));
            return;
        }

        if (unresolvedChipKeys.length > 0) {
            lifecycle.hasResolvedMultipleAIField = false;
            return;
        }

        if (lifecycle.hasResolvedMultipleAIField) {
            return;
        }

        lifecycle.hasResolvedMultipleAIField = true;
        setOptions([]);
        updateAIDataResolved ? updateAIDataResolved(name) : setIsAIExtractedValue(false);
    }, [getChipKeys, isAIExtractedData, multiple, name, unresolvedChipKeys, updateAIDataResolved, val, setOptions]);

    useEffect(() => {
        const lifecycle = aiLifecycleRef.current;

        if (!preload) {
            lifecycle.hasInitializedPreloadSearch = false;
            return;
        }

        if (lifecycle.hasInitializedPreloadSearch) {
            return;
        }

        lifecycle.hasInitializedPreloadSearch = true;

        const initialSingleModeSearchTerm =
            !multiple && !isAIExtractedData
                ? typeof val === 'string'
                    ? val
                    : typeof val === 'object' && val?.label
                      ? val.label
                      : ''
                : '';

        const preloadTerm = isAIExtractedData ? queryTerm : initialSingleModeSearchTerm;
        updateOptions(preloadTerm, true, effectiveFilter);
    }, [effectiveFilter, isAIExtractedData, multiple, preload, queryTerm, updateOptions, val]);

    useEffect(() => {
        const lifecycle = aiLifecycleRef.current;

        if (preload || isAIExtractedData) {
            lifecycle.hasInitializedRegularFlowPrefetch = false;
            return;
        }

        if (lifecycle.hasInitializedRegularFlowPrefetch) {
            return;
        }

        // Keep legacy regular-flow behavior: issue one initial search on mount even when preload is false.
        lifecycle.hasInitializedRegularFlowPrefetch = true;

        const initialSingleModeSearchTerm = !multiple
            ? typeof val === 'string'
                ? val
                : typeof val === 'object' && val?.label
                  ? val.label
                  : ''
            : '';

        updateOptions(initialSingleModeSearchTerm, true, effectiveFilter);
    }, [effectiveFilter, isAIExtractedData, multiple, preload, updateOptions, val]);

    useEffect(() => {
        const lifecycle = aiLifecycleRef.current;

        if (multiple || !isAIExtractedData) {
            lifecycle.lastSingleAIPrefetchTerm = null;
            return;
        }

        const searchTerm = typeof val === 'object' && val?.label ? val.label : '';
        if (!searchTerm) {
            return;
        }

        if (lifecycle.lastSingleAIPrefetchTerm === searchTerm) {
            return;
        }

        // Keep single-select AI behavior: load matching options before user interaction.
        lifecycle.lastSingleAIPrefetchTerm = searchTerm;
        updateOptions(searchTerm, true, effectiveFilter);
    }, [effectiveFilter, isAIExtractedData, multiple, updateOptions, val]);

    const unresolvedChipSx = useMemo(
        () => ({
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: requiresUserCorrection ? aiExtractedFieldColor.HIGHLIGHTED : aiExtractedFieldColor.NORMAL
        }),
        [aiExtractedFieldColor.HIGHLIGHTED, aiExtractedFieldColor.NORMAL, requiresUserCorrection]
    );

    const getChipSx = useCallback(
        (chipKey: string) => {
            if (!unresolvedChipKeySet.has(chipKey)) {
                return undefined;
            }

            return unresolvedChipSx;
        },
        [unresolvedChipSx, unresolvedChipKeySet]
    );

    return {
        isAIExtractedValue,
        setIsAIExtractedValue,
        unresolvedChipKeys,
        editingChipKey,
        setEditingChipKey,
        editingChipSearchTerm,
        setEditingChipSearchTerm,
        isAutocompleteOpen,
        setIsAutocompleteOpen,
        unresolvedChipKeySet,
        isMultipleAIFieldActive,
        isAIFieldActive,
        getChipSx,
        syncUnresolvedChipKeys,
        onAutocompleteOpen,
        onAutocompleteClose,
        onUnresolvedChipClick,
        clearChipEditingContext
    };
};
